import { useMemo, useState } from "react";
import { useCountryPricing } from "./useCountryPricing";

export interface PhoneCountry {
  code: string; // ISO 3166-1 alpha-2
  name: string;
  isdCode: string; // International calling code without the plus sign
  flag: string; // URL to flag image
}

const flagUrl = (code: string) => `https://flagcdn.com/w20/${code.toLowerCase()}.png`;

// Curated list; extend freely without touching consumers of the hook
const BASE_PHONE_COUNTRIES: Omit<PhoneCountry, "flag">[] = [
  { code: "US", name: "United States", isdCode: "1" },
  { code: "IN", name: "India", isdCode: "91" },
  { code: "GB", name: "United Kingdom", isdCode: "44" },
  { code: "CA", name: "Canada", isdCode: "1" },
  { code: "AE", name: "United Arab Emirates", isdCode: "971" },
  { code: "AU", name: "Australia", isdCode: "61" },
  { code: "NZ", name: "New Zealand", isdCode: "64" },
  { code: "SG", name: "Singapore", isdCode: "65" },
  { code: "DE", name: "Germany", isdCode: "49" },
  { code: "FR", name: "France", isdCode: "33" },
  { code: "BR", name: "Brazil", isdCode: "55" },
  { code: "ID", name: "Indonesia", isdCode: "62" },
  { code: "IR", name: "Iran", isdCode: "98" },
  { code: "IS", name: "Iceland", isdCode: "354" },
  { code: "HU", name: "Hungary", isdCode: "36" },
];

export interface UsePhoneCountryReturn {
  countries: PhoneCountry[];
  selectedCountryCode: string;
  setSelectedCountryCode: (code: string) => void;
  selected?: PhoneCountry;
  isdPrefix: string; // e.g., "+91"
  formatInternational: (raw: string) => string; // returns "+<isd> <raw>"
}

export const usePhoneCountry = (): UsePhoneCountryReturn => {
  const { countryCode: detected } = useCountryPricing();
  const countries = useMemo<PhoneCountry[]>(
    () => BASE_PHONE_COUNTRIES.map((c) => ({ ...c, flag: flagUrl(c.code) })),
    []
  );

  const [selectedCountryCode, setSelectedCountryCode] = useState<string>(
    countries.find((c) => c.code === detected)?.code || "US"
  );

  const selected = useMemo(
    () => countries.find((c) => c.code === selectedCountryCode),
    [countries, selectedCountryCode]
  );

  const isdPrefix = useMemo(() => (selected ? `+${selected.isdCode}` : "+"), [selected]);

  const formatInternational = (raw: string) => `${isdPrefix} ${raw || ""}`.trim();

  return {
    countries,
    selectedCountryCode,
    setSelectedCountryCode,
    selected,
    isdPrefix,
    formatInternational,
  };
};

